import { mapActions, mapGetters } from 'vuex';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import widgetEmptyState from '@/components/generics/widget-empty-state/widget-empty-state';
import { getClosingStatus } from '@/components/pinning/pinboard-closing-status';
import { normalizePortfolioSort } from '@/components/pinning/pinboard-portfolio-sort';
import { getVolumeStatus, getDateStatus } from '@/components/pinning/pinboard-trade-status';
import pinboardWidgetItem from '@/components/pinning/pinboard-widget-item/pinboard-widget-item.vue';
import pinboardWidgetSkeleton from '@/components/pinning/pinboard-widget-skeleton/pinboard-widget-skeleton.vue';
import loaderMixin from '@/mixins/loader';
import Contract from '@/models/contract';
import { WIDGET_STATUS } from '@/store/modules/widgets';

const CLOSING_STATUS_REFRESH_INTERVAL = 30 * 1000;

export default {
  mixins: [loaderMixin],
  components: { pinboardWidgetItem, widgetEmptyState, pinboardWidgetSkeleton },
  data() {
    return {
      visibleCount: this.$store.getters['pinning/getWidgetItemsPerPage']('portfolio'),
      now: Date.now(),
      closingStatusInterval: null,
    };
  },
  computed: {
    ...mapGetters('widgets', ['getWidget']),
    ...mapGetters('pinning', ['getWidgetSort', 'getWidgetShowPositions', 'getWidgetItemsPerPage']),
    ...mapGetters('user', ['isLoggedIn']),
    sortValue() {
      return normalizePortfolioSort(this.getWidgetSort('portfolio'));
    },
    sortKey() {
      return `${this.sortValue.sort}:${this.sortValue.sortParameter}`;
    },
    flatItems() {
      const markets = this.getWidget('portfolio').data || [];
      const items = [];
      markets.forEach((market) => {
        (market.marketContracts || []).forEach((c) => {
          const contract = new Contract(c);
          items.push({
            marketId: market.marketId,
            marketName: market.marketName,
            contractId: contract.contractId,
            contractName: contract.contractName,
            lastTradePriceAsCents: contract.lastTradePriceAsCents,
            changePriceAsCents: contract.changePriceAsCents,
            lastClosePrice: contract.lastClosePrice,
            hasLastTradePrice: contract.hasLastTradePrice,
            positionMode: contract.mode,
            contract: c,
            closing:
              market.marketDateEnd || c.isLive === true
                ? {
                    dateEnd: market.marketDateEnd,
                    isOpen: contract.contractIsOpen,
                    isLive: c.isLive === true,
                  }
                : null,
            tradeStatus: getVolumeStatus(market.marketSharesTraded),
            newestStatus: getDateStatus(market.marketDateOpened),
          });
        });
      });
      return items;
    },
    showClosing() {
      return this.sortValue.sort === 'CLOSING';
    },
    isClosingRefreshActive() {
      return this.showClosing && this.isLoggedIn;
    },
    showTraded() {
      return this.sortValue.sort === 'TRADED';
    },
    showNewest() {
      return this.sortValue.sort === 'NEWEST';
    },
    itemsPerPage() {
      return this.getWidgetItemsPerPage('portfolio');
    },
    visibleItems() {
      return this.flatItems.slice(0, this.visibleCount);
    },
    canLoadMore() {
      return this.visibleCount < this.flatItems.length;
    },
    hasError() {
      return this.getWidget('portfolio').status === WIDGET_STATUS.ERROR;
    },
    showPositionsBar() {
      return this.getWidgetShowPositions('portfolio');
    },
  },
  methods: {
    ...mapActions('widgets', ['fetchMyShares']),
    ...mapActions('popup', ['setCurrentPopup']),
    onLogin() {
      this.setCurrentPopup(MODALS.LOGIN);
    },
    loadData(force = false) {
      this.loaderAdd();
      this.fetchMyShares({
        sort: this.sortValue.sort || undefined,
        sortParameter: this.sortValue.sortParameter || undefined,
        force,
      })
        .then(() => this.loaderRemove())
        .catch(() => this.loaderRemove());
    },
    loadMore() {
      this.visibleCount += this.itemsPerPage;
    },
    itemStatusInfo(item) {
      if (this.showClosing) {
        const { closing } = item;
        return closing
          ? getClosingStatus(closing.dateEnd, closing.isOpen, this.now, closing.isLive)
          : null;
      }
      if (this.showTraded) return item.tradeStatus;
      if (this.showNewest) return item.newestStatus;
      return null;
    },
    stopClosingStatusRefresh() {
      if (this.closingStatusInterval) {
        clearInterval(this.closingStatusInterval);
        this.closingStatusInterval = null;
      }
    },
    itemLink(marketId, contractId) {
      const query = {};
      if (contractId) {
        query.contractId = contractId;
      }
      return { name: 'market-details', params: { marketId }, query };
    },
  },
  watch: {
    isLoggedIn(val) {
      if (val) this.loadData();
    },
    sortKey() {
      if (!this.isLoggedIn) return;
      this.visibleCount = this.itemsPerPage;
      this.loadData(true);
    },
    itemsPerPage(val) {
      this.visibleCount = val;
    },
    isClosingRefreshActive: {
      handler(val) {
        this.stopClosingStatusRefresh();
        if (!val) return;
        this.now = Date.now();
        this.closingStatusInterval = setInterval(() => {
          this.now = Date.now();
        }, CLOSING_STATUS_REFRESH_INTERVAL);
      },
      immediate: true,
    },
  },
  mounted() {
    if (!this.isLoggedIn) return;
    this.loadData();
  },
  beforeUnmount() {
    this.stopClosingStatusRefresh();
  },
};
